/**
 * Lesson Fee Calculation Utility
 * 
 * Calculates lesson fees using the same priority logic as the backend:
 * 1. customLessonFee (explicit override by tutor)
 * 2. lesson.hourlyRate or lesson.rate (lesson-specific rate)
 * 3. location.hourlyRate (fallback to location default)
 * 
 * This ensures consistent fee calculations across the app.
 */

import { loggers } from './logger';

export interface LessonFeeResult {
  lessonFee: number;
  hourlyRate: number;
  feeSource: 'customLessonFee' | 'lesson.hourlyRate' | 'lesson.rate' | 'location.hourlyRate' | 'none' | 'cancelled';
  calculation: string;
}

export interface LessonForFeeCalculation {
  id?: string;
  duration?: number;
  customLessonFee?: number;
  hourlyRate?: number;
  rate?: number;
  lessonFee?: number;
  status?: string;
  locationId?: string;
}

export interface LocationForFeeCalculation {
  id?: string;
  hourlyRate?: number;
}

/**
 * Calculate lesson fee based on lesson data and optionally location data
 * 
 * Priority:
 * 1. If cancelled and lessonFee already set, use it (policy already applied)
 * 2. customLessonFee - explicit custom fee set by tutor
 * 3. lesson.hourlyRate - lesson-specific hourly rate
 * 4. lesson.rate - alternative rate field
 * 5. location.hourlyRate - location's default hourly rate
 * 
 * @param lesson - Lesson object with fee/rate fields
 * @param location - Optional location object with hourlyRate
 * @returns LessonFeeResult with calculated fee and source info
 */
export function calculateLessonFee(
  lesson: LessonForFeeCalculation,
  location?: LocationForFeeCalculation | null
): LessonFeeResult {
  const durationMinutes = lesson.duration || 30; // Default to 30 minutes
  
  loggers.lessons.log(`💰 Calculating fee for lesson ${lesson.id}: duration=${durationMinutes}min`);
  
  // Priority 0: If cancelled and lessonFee already set, use it
  if (lesson.status === 'cancelled' && lesson.lessonFee !== undefined && lesson.lessonFee !== null) {
    loggers.lessons.log(`💰 Using stored lessonFee for cancelled lesson: $${lesson.lessonFee}`);
    return {
      lessonFee: lesson.lessonFee,
      hourlyRate: lesson.lessonFee / (durationMinutes / 60),
      feeSource: 'cancelled',
      calculation: `Cancellation fee: $${lesson.lessonFee.toFixed(2)}`
    };
  }
  
  // Priority 1: customLessonFee (explicit override by tutor)
  if (lesson.customLessonFee !== undefined && lesson.customLessonFee !== null && lesson.customLessonFee > 0) {
    loggers.lessons.log(`💰 Using customLessonFee: $${lesson.customLessonFee}`);
    return {
      lessonFee: lesson.customLessonFee,
      hourlyRate: lesson.customLessonFee / (durationMinutes / 60),
      feeSource: 'customLessonFee',
      calculation: `Custom fee: $${lesson.customLessonFee.toFixed(2)}`
    };
  }
  
  let hourlyRate = 0;
  let feeSource: LessonFeeResult['feeSource'] = 'none';
  
  // Priority 2: lesson.hourlyRate
  if (lesson.hourlyRate !== undefined && lesson.hourlyRate !== null && lesson.hourlyRate > 0) {
    hourlyRate = lesson.hourlyRate;
    feeSource = 'lesson.hourlyRate';
    loggers.lessons.log(`💰 Using lesson.hourlyRate: $${hourlyRate}/hr`);
  }
  // Priority 3: lesson.rate
  else if (lesson.rate !== undefined && lesson.rate !== null && lesson.rate > 0) {
    hourlyRate = lesson.rate;
    feeSource = 'lesson.rate';
    loggers.lessons.log(`💰 Using lesson.rate: $${hourlyRate}/hr`);
  }
  // Priority 4: location.hourlyRate (fallback)
  else if (location && location.hourlyRate !== undefined && location.hourlyRate !== null && location.hourlyRate > 0) {
    hourlyRate = location.hourlyRate;
    feeSource = 'location.hourlyRate';
    loggers.lessons.log(`💰 Using location.hourlyRate: $${hourlyRate}/hr (location: ${location.id})`);
  }
  else {
    loggers.lessons.log(`💰 No valid rate found, returning $0`);
    return {
      lessonFee: 0,
      hourlyRate: 0,
      feeSource: 'none',
      calculation: 'No rate available'
    };
  }
  
  // Calculate: hourlyRate × (duration / 60)
  const durationHours = durationMinutes / 60;
  const lessonFee = Math.round(hourlyRate * durationHours * 100) / 100; // Round to 2 decimal places
  const calculation = `$${hourlyRate.toFixed(2)}/hr × ${durationHours}hrs = $${lessonFee.toFixed(2)}`;
  
  loggers.lessons.log(`💰 Calculated fee: ${calculation} (source: ${feeSource})`);
  
  return {
    lessonFee,
    hourlyRate,
    feeSource,
    calculation
  };
}

/**
 * Calculate cancellation fee based on lesson fee and policy percentage
 * 
 * @param lessonFee - Original lesson fee in dollars
 * @param feePercentage - Fee percentage (0-100)
 * @returns Object with calculated fee and description
 */
export function calculateCancellationFee(
  lessonFee: number,
  feePercentage: number
): { feeDollars: number; calculation: string } {
  const feeDollars = Math.round(lessonFee * feePercentage) / 100;
  const calculation = `$${lessonFee.toFixed(2)} × ${feePercentage}% = $${feeDollars.toFixed(2)}`;
  
  loggers.lessons.log(`💰 Cancellation fee: ${calculation}`);
  
  return {
    feeDollars,
    calculation
  };
}

/**
 * Get lesson fee with location lookup from DataManager
 * This is a convenience wrapper for use with DataManager context
 * 
 * @param lesson - Lesson object
 * @param getLocationById - Function to get location by ID (from DataManager)
 * @returns LessonFeeResult
 */
export function getLessonFeeWithLocationLookup(
  lesson: LessonForFeeCalculation,
  getLocationById: (id: string | undefined | null) => LocationForFeeCalculation | undefined
): LessonFeeResult {
  const location = lesson.locationId ? getLocationById(lesson.locationId) : undefined;
  return calculateLessonFee(lesson, location);
}
